import { AddonIframeElementData, AddonIframeElementSettingData } from '@/components/addons/iframe-element/Data';
import { AlignContentType, BooleanDataType, IframeWidthType } from '@/typings/enums/enums';
import useDevice from '@/hooks/useDevice';
import AddonModel from '@/models/grid/AddonModel';
import { AddonModelState } from '@/typings/interfaces/state/Addon';
import { ClassList } from '@/typings/types/types';
import { CSSProperties } from 'vue';

interface AddonIframeElementSettingsState {
  alignment: AlignContentType;
  fixedHeight: number;
  fixedWidth?: number;
  responsiveHeight: boolean;
  showScrollbars: boolean;
  width: IframeWidthType;
  url: string;
}

export interface AddonIframeElementState extends AddonModelState {
  settings: AddonIframeElementSettingsState;
  lastKnownIframeHeight?: number;
  elementStyling?: {
    layout?: CSSProperties;
    color?: CSSProperties;
  };
  wrapperClasses: ClassList;
}

export class AddonIframeElementModel extends AddonModel<AddonIframeElementData, AddonIframeElementState> {
  parseAddon(data: AddonIframeElementData) {
    const state = this.state;

    state.settings = state.settings ?? {};

    if (data.settings) {
      state.settings = AddonIframeElementModel.getParsedSettingsDate(data.settings);
    }

    state.wrapperClasses = AddonIframeElementModel.constructIframeWrapperClasses(state.settings);

    state.elementStyling = state.elementStyling ?? {};
    if (state.elementStyling.layout) {
      state.elementStyling.layout.height = state.elementStyling.layout.height ?? undefined;
    }

    state.elementStyling.layout = AddonIframeElementModel.constructIframeElementLayoutStyles(
      state.settings,
      (state.lastKnownIframeHeight ?? '300px') + ''
    );

    if (state.settings.fixedWidth && state.settings.width !== IframeWidthType.FIXED) {
      state.elementStyling.layout.width = undefined;
    }
  }

  private static getParsedSettingsDate = (data: AddonIframeElementSettingData): AddonIframeElementSettingsState => {
    const { isMobile } = useDevice();
    const url = data.url ? data.url : '';

    if (isMobile && data.overwrite_mobile === BooleanDataType.FALSE && data.mobile) {
      return AddonIframeElementModel.constructSettingState(data.mobile, url);
    }

    return AddonIframeElementModel.constructSettingState(data, url);
  };

  private static constructSettingState(
    data: AddonIframeElementSettingData,
    url: string
  ): AddonIframeElementSettingsState {
    return {
      url: url,
      fixedHeight: data.fixed_height ? Number(data.fixed_height) : 300,
      ...(data.fixed_width && { fixedWidth: Number(data.fixed_width) }),
      showScrollbars: data.show_scrollbars === '1',
      responsiveHeight: data.responsive_height === '1',
      width: data.width ? data.width : IframeWidthType.CONTENT,
      alignment: data.alignment ? data.alignment : AlignContentType.CENTER
    };
  }

  private static constructIframeElementLayoutStyles(
    state: AddonIframeElementSettingsState,
    responsiveHeight: string
  ): CSSProperties {
    let height: CSSProperties['height'];

    if (!state.responsiveHeight) {
      height = state.fixedHeight + 'px';
    } else {
      height = responsiveHeight;
    }

    return {
      ...(state?.fixedWidth && { width: `${state.fixedWidth}px` }),
      ...{ height: height }
    };
  }

  private static constructIframeWrapperClasses(settings: AddonIframeElementSettingsState): ClassList {
    return {
      'iframe-element--full-width':
        settings.width !== IframeWidthType.FIXED && settings.width !== IframeWidthType.CONTENT,
      'iframe-element--fixed-width': settings.width === IframeWidthType.FIXED,
      [`iframe-element--align-${settings.alignment}`]: !!settings.alignment
    };
  }

  // if path is width then value on fixed width should be undefined
  public setValue(path: string, value: string | number | boolean, shouldParse?: boolean) {
    const data = this.getData();
    // fixed_width should be undefined
    if (path === 'width' && value !== 'fixed') {
      delete data.settings.fixed_width;
      delete data.settings.alignment;
    }

    super.setValue(path, value, shouldParse);
  }

  public isAddonValid(): boolean {
    const data = this.getData();
    return !(!data.settings?.url || data.settings?.url === '');
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen';
  }
}
